import { Clock, Zap } from "lucide-react";
import { SpotlightOrbs } from "./SpotlightOrbs";
import { NoiseTexture } from "./NoiseTexture";
import { AnimatedCounter } from "./AnimatedCounter";
import { useScrollProgress } from "@/hooks/useScrollProgress";
import { useAnimateOnScroll } from "@/hooks/useAnimateOnScroll";

interface MarketData {
  market_median_dom: number;
  market_active_listings: number;
  market_sold_listings: number;
  avg_demand_rating: number;
  neighborhoods_with_data: number;
  market_price_reduction_rate: number;
  team_price_reduction_rate: number;
}

interface SpotlightMarketComparisonProps {
  market: MarketData;
  teamAvgDom: number;
  primaryColor?: string | null;
}

function getDomInsight(teamDom: number, marketDom: number): { headline: string; body: string; positive: boolean } {
  if (marketDom === 0) return { headline: "", body: "", positive: true };
  const diff = teamDom - marketDom;
  const pct = Math.round(Math.abs(diff / marketDom) * 100);
  const daysDiff = Math.abs(diff);

  if (diff < -5 && pct >= 30) {
    return {
      headline: `${pct}% faster than the market average`,
      body: `Our listings sell ${daysDiff} days faster than the typical home in the area. A focused pricing strategy, professional marketing, and proactive buyer outreach mean less time waiting and fewer price reductions for our sellers.`,
      positive: true,
    };
  }
  if (diff < -1) {
    return {
      headline: `${pct}% faster than the market average`,
      body: `Our homes consistently go under contract ahead of the local average. Strategic preparation, targeted marketing, and strong negotiation skills help our sellers move quickly and on favorable terms.`,
      positive: true,
    };
  }
  if (diff > 5) {
    return {
      headline: `Selling in a competitive market`,
      body: `Even in a slower market, our team delivers results through expert pricing guidance, professional staging consultation, and comprehensive marketing plans that maximize exposure to qualified buyers.`,
      positive: false,
    };
  }
  return {
    headline: "Right on pace with the market",
    body: "Our listings sell in line with the local market, reflecting precise pricing and a disciplined sales approach that delivers predictable results for our clients.",
    positive: true,
  };
}

export function SpotlightMarketComparison({
  market,
  teamAvgDom,
  primaryColor,
}: SpotlightMarketComparisonProps) {
  const { ref: scrollRef, progress } = useScrollProgress();
  const { ref: animRef, isVisible } = useAnimateOnScroll(0.15);

  if (market.neighborhoods_with_data === 0) return null;

  const accentColor = primaryColor ?? "#3B82F6";
  const parallaxY = (progress - 0.5) * -30;

  const insight = getDomInsight(teamAvgDom, market.market_median_dom);

  // DOM bar widths (team bar is always shorter when faster = better)
  const maxDom = Math.max(teamAvgDom, market.market_median_dom, 1);
  const teamBarPct = (teamAvgDom / maxDom) * 100;
  const marketBarPct = (market.market_median_dom / maxDom) * 100;

  // Show price reductions when team is favorable, otherwise show AI market insight
  const showPriceReductions = market.team_price_reduction_rate <= market.market_price_reduction_rate;

  // Demand gauge
  const demandPct = (market.avg_demand_rating / 10) * 100;
  const circumference = 2 * Math.PI * 54;
  const strokeDash = isVisible ? (demandPct / 100) * circumference : 0;
  const demandColor = market.avg_demand_rating >= 7 ? "#22c55e" : market.avg_demand_rating >= 5 ? "#f59e0b" : "#ef4444";

  return (
    <section
      ref={scrollRef}
      className="relative w-full py-24 md:py-32 px-6 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)",
      }}
    >
      <SpotlightOrbs primaryColor={primaryColor} />
      <NoiseTexture />

      <div
        ref={animRef}
        className="relative z-10 max-w-7xl mx-auto will-change-transform"
        style={{ transform: `translateY(${parallaxY}px)` }}
      >
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/40 mb-4">
          Market Comparison
        </p>

        {/* Headline with insight */}
        {insight.headline && (
          <h3
            className={`text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mb-16 transition-all duration-1000 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
            }`}
          >
            {insight.headline}
          </h3>
        )}

        <div
          className={`grid grid-cols-1 lg:grid-cols-5 items-stretch gap-10 lg:gap-16 transition-all duration-700 delay-200 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {/* LEFT: DOM visual comparison — takes 3 cols */}
          <div className="lg:col-span-3 flex">
            <div className={`rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-8 md:p-10 flex-1 flex flex-col ${!showPriceReductions ? "justify-center" : ""}`}>
              <div className="flex items-center gap-2 mb-8">
                <Clock className="h-4 w-4 text-white/40" />
                <p className="text-white/60 text-sm uppercase tracking-wider">Days on Market</p>
              </div>

              {/* Horizontal bar comparison */}
              <div className="space-y-6">
                {/* Team bar */}
                <div>
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4" style={{ color: accentColor }} />
                      <span className="text-sm font-medium text-white">Our Team</span>
                    </div>
                    <AnimatedCounter
                      value={`${teamAvgDom} days`}
                      className="text-2xl md:text-3xl font-bold text-white block"
                      duration={1800}
                    />
                  </div>
                  <div className="h-4 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-[1500ms] ease-out"
                      style={{
                        width: isVisible ? `${Math.max(teamBarPct, 8)}%` : "0%",
                        backgroundColor: accentColor,
                      }}
                    />
                  </div>
                </div>

                {/* Market bar */}
                <div>
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-sm font-medium text-white/50">Market Average</span>
                    <span className="text-2xl md:text-3xl font-bold text-white/40">{market.market_median_dom} days</span>
                  </div>
                  <div className="h-4 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-white/20 transition-all duration-[1500ms] ease-out"
                      style={{
                        width: isVisible ? `${Math.max(marketBarPct, 8)}%` : "0%",
                        transitionDelay: "300ms",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* AI insight paragraph */}
              {insight.body && (
                <p
                  className={`text-white/50 text-sm leading-relaxed mt-8 max-w-xl transition-all duration-700 delay-700 ${
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  }`}
                >
                  {insight.body}
                </p>
              )}

              {/* Bottom stats — price reductions (only when favorable to team) */}
              {showPriceReductions && (
                <div className="mt-auto pt-8">
                  <div className="border-t border-white/10 pt-6">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-4">Price Reductions</p>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-3xl md:text-4xl font-bold text-white">{market.team_price_reduction_rate}%</span>
                        </div>
                        <p className="text-white/50 text-sm mt-1">Our Listings</p>
                        <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-[1500ms] ease-out"
                            style={{
                              width: isVisible ? `${Math.max(market.team_price_reduction_rate, 2)}%` : "0%",
                              backgroundColor: accentColor,
                              transitionDelay: "600ms",
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-3xl md:text-4xl font-bold text-white/40">{market.market_price_reduction_rate}%</span>
                        </div>
                        <p className="text-white/30 text-sm mt-1">Market Average</p>
                        <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-white/20 transition-all duration-[1500ms] ease-out"
                            style={{
                              width: isVisible ? `${Math.max(market.market_price_reduction_rate, 2)}%` : "0%",
                              transitionDelay: "900ms",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <p
                      className={`text-white/40 text-xs mt-4 leading-relaxed transition-all duration-700 delay-[1000ms] ${
                        isVisible ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      {market.team_price_reduction_rate < market.market_price_reduction_rate
                        ? "Fewer price reductions means our listings are priced right from day one."
                        : "Our price reduction rate matches the local market average."}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Demand rating — takes 2 cols */}
          <div className="lg:col-span-2 flex">
            <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-8 md:p-10 flex-1 flex flex-col items-center justify-center">
              <p className="text-white/60 text-sm uppercase tracking-wider mb-8">Market Demand</p>

              <div className="relative w-40 h-40 md:w-48 md:h-48">
                <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                  <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
                  <circle
                    cx="60"
                    cy="60"
                    r="54"
                    fill="none"
                    stroke={demandColor}
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeDasharray={`${strokeDash} ${circumference}`}
                    className="transition-all duration-[2000ms] ease-out"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl md:text-5xl font-bold text-white">{market.avg_demand_rating}</span>
                  <span className="text-white/30 text-sm">/10</span>
                </div>
              </div>

              <div
                className={`text-center mt-6 transition-all duration-700 delay-500 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
              >
                <p className="text-lg font-semibold" style={{ color: demandColor }}>
                  {market.avg_demand_rating >= 7 ? "High Demand" : market.avg_demand_rating >= 5 ? "Moderate Demand" : "Low Demand"}
                </p>
                <p className="text-white/40 text-sm mt-2 leading-relaxed max-w-[16rem]">
                  {market.avg_demand_rating >= 8
                    ? "Homes are selling fast with strong competition among buyers. Sellers are in a favorable position."
                    : market.avg_demand_rating >= 7
                      ? "Buyer activity is strong and well-priced homes are moving quickly with multiple offers common."
                      : market.avg_demand_rating >= 5
                        ? "The market is balanced with steady buyer interest. Properly priced homes sell within a reasonable timeframe."
                        : market.avg_demand_rating >= 3
                          ? "Buyer activity is slower than average. Pricing strategy and presentation are key to attracting offers."
                          : "A buyer-friendly market with less competition. Sellers may need to be flexible on pricing and terms."}
                </p>
                <p className="text-white/30 text-xs mt-3">
                  Based on {market.neighborhoods_with_data} neighborhood{market.neighborhoods_with_data !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
